import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  FormControl,
  Select,
  MenuItem,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { DataService } from 'src/services/data-service';

interface Lease {
  id: string;
  refNumber: string;
  tenant: {
    fullName: string;
  };
  unit: {
    id: string;
    number: string;
  };
}

interface Installment {
  id: string;
  status: string;
  total: number;
  dueDate: string;
}

interface ApplianceType {
  id: string;
  name: string;
  description: string;
  active: boolean;
}

interface ApplianceCheckOut {
  applianceId: string;
  conditionStatus: string;
  notes: string;
}

interface CheckOutDialogProps {
  open: boolean;
  onClose: () => void;
  lease: Lease;
  onSuccess: () => void;
}

const CheckOutDialog: React.FC<CheckOutDialogProps> = ({ open, onClose, lease, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [appliancesLoading, setAppliancesLoading] = useState(false);
  const [installmentsLoading, setInstallmentsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [applianceTypes, setApplianceTypes] = useState<ApplianceType[]>([]);
  const [checkOutDate, setCheckOutDate] = useState<string>(new Date().toISOString().slice(0, 16));
  const [appliances, setAppliances] = useState<ApplianceCheckOut[]>([]);
  const [installments, setInstallments] = useState<Installment[]>([]);
  const [hasUnpaidInstallments, setHasUnpaidInstallments] = useState(false);

  useEffect(() => {
    if (open && lease.id) {
      fetchCheckInAppliances();
      fetchApplianceTypes();
      fetchInstallments();
    }
  }, [open, lease.id]);

  const fetchApplianceTypes = async () => {
    try {
      const response = await DataService.get('api/appliance/all');

      if (response.ok) {
        const responseData = await response.json();
        let typesList: ApplianceType[] = [];

        if (responseData.success && responseData.data) {
          typesList = responseData.data;
        } else if (Array.isArray(responseData)) {
          typesList = responseData;
        }

        setApplianceTypes(typesList.filter(type => type.active));
      } else {
        throw new Error('Failed to fetch appliance types');
      }
    } catch (error) {
      console.error('Error fetching appliance types:', error);
    }
  };

  const fetchCheckInAppliances = async () => {
    setAppliancesLoading(true);
    setError(null);
    try {
      const response = await DataService.get(`api/Lease/appliances-conditions/${lease.id}`);

      if (response.ok) {
        const responseData = await response.json();
        let checkInAppliances: ApplianceCheckOut[] = [];

        if (responseData.success && responseData.data) {
          if (responseData.data.checkinAppliancesConditions) {
            checkInAppliances = responseData.data.checkinAppliancesConditions;
          }
        }

        setAppliances(checkInAppliances);
      } else {
        throw new Error('Failed to fetch check-in appliances');
      }
    } catch (error) {
      console.error('Error fetching check-in appliances:', error);
      setError('Failed to load check-in appliances. Please try again.');
    } finally {
      setAppliancesLoading(false);
    }
  };

  const fetchInstallments = async () => {
    setInstallmentsLoading(true);
    try {
      const response = await DataService.get(`api/Installment/lease/${lease.id}`);

      if (response.ok) {
        const responseData = await response.json();
        let installmentsList: Installment[] = [];

        if (responseData.success && responseData.data) {
          installmentsList = responseData.data;
        } else if (Array.isArray(responseData)) {
          installmentsList = responseData;
        }

        setInstallments(installmentsList);

        // Check if there are any unpaid installments
        const unpaid = installmentsList.some(
          (inst) => inst.status !== 'Paid' && inst.status !== 'Completed' && inst.status !== 'Cancelled'
        );
        setHasUnpaidInstallments(unpaid);

        if (unpaid) {
          setError('Cannot proceed with check-out: There are unpaid installments for this lease. Please ensure all installments are paid before checking out.');
        }
      } else {
        throw new Error('Failed to fetch installments');
      }
    } catch (error) {
      console.error('Error fetching installments:', error);
    } finally {
      setInstallmentsLoading(false);
    }
  };

  const handleApplianceChange = (index: number, field: keyof ApplianceCheckOut, value: string) => {
    const updatedAppliances = [...appliances];
    updatedAppliances[index] = {
      ...updatedAppliances[index],
      [field]: value
    };
    setAppliances(updatedAppliances);
  };

  const getApplianceName = (applianceId: string): string => {
    const appliance = applianceTypes.find(type => type.id === applianceId);
    return appliance ? appliance.name : applianceId;
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      const payload = {
        leaseId: lease.id,
        date: new Date(checkOutDate).toISOString(),
        appliances: appliances
      };

      const response = await DataService.post('api/Lease/checkout', payload);

      if (response.ok) {
        onSuccess();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to submit check-out');
      }
    } catch (error) {
      console.error('Error submitting check-out:', error);
      setError(error instanceof Error ? error.message : 'Failed to submit check-out. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle sx={{ bgcolor: 'warning.main', color: 'white', fontWeight: 'bold', py: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LogoutIcon />
          Check Out - Lease #{lease.refNumber}
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2, pb: 1 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mb: 1.5 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 1.5, bgcolor: 'grey.50', height: '100%' }}>
              <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block" sx={{ mb: 0.5 }}>
                Lease Information
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.25 }}>
                <strong>Tenant:</strong> {lease.tenant.fullName}
              </Typography>
              <Typography variant="body2">
                <strong>Unit:</strong> {lease.unit.number}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Check-Out Date & Time"
              type="datetime-local"
              value={checkOutDate}
              onChange={(e) => setCheckOutDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              size="small"
              required
            />
          </Grid>
        </Grid>

        {/* Installments Status */}
        {installmentsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 1.5 }}>
            <CircularProgress size={20} />
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              Checking installments...
            </Typography>
          </Box>
        ) : hasUnpaidInstallments ? (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            <strong>Unpaid Installments Found!</strong>
            <br />
            There are {installments.filter(inst => inst.status !== 'Paid' && inst.status !== 'Completed' && inst.status !== 'Cancelled').length} unpaid installment(s) for this lease. All installments must be paid before proceeding with check-out.
          </Alert>
        ) : installments.length > 0 ? (
          <Alert severity="success" sx={{ mb: 1.5 }}>
            All installments are paid. You can proceed with check-out.
          </Alert>
        ) : null}

        <Divider sx={{ mb: 1.5 }} />

        <Box sx={{ mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            Appliances From Check-In (Update Condition & Notes)
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Review and update the condition of appliances from check-in
          </Typography>
        </Box>

        {appliancesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '150px' }}>
            <CircularProgress />
          </Box>
        ) : appliances.length === 0 ? (
          <Alert severity="warning" sx={{ mb: 1 }}>
            No check-in appliances found for this lease. Please complete check-in first.
          </Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.75, px: 1 } }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 'bold', width: '5%', py: 0.5 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '35%', py: 0.5 }}>Appliance Type</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '25%', py: 0.5 }}>Check-Out Condition</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '35%', py: 0.5 }}>Check-Out Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {appliances.map((appliance, index) => (
                  <TableRow key={index} hover>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {getApplianceName(appliance.applianceId)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Check-in: {appliance.conditionStatus}
                        {appliance.notes && ` - ${appliance.notes}`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <FormControl fullWidth size="small" required>
                        <Select
                          value={appliance.conditionStatus}
                          onChange={(e) => handleApplianceChange(index, 'conditionStatus', e.target.value)}
                        >
                          <MenuItem value="Excellent">Excellent</MenuItem>
                          <MenuItem value="Good">Good</MenuItem>
                          <MenuItem value="Fair">Fair</MenuItem>
                          <MenuItem value="Poor">Poor</MenuItem>
                          <MenuItem value="Damaged">Damaged</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={appliance.notes}
                        onChange={(e) => handleApplianceChange(index, 'notes', e.target.value)}
                        placeholder="Add check-out observations..."
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
          size="small"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="warning"
          size="small"
          disabled={loading || appliancesLoading || installmentsLoading || appliances.length === 0 || hasUnpaidInstallments}
          startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
        >
          {loading ? 'Submitting...' : 'Submit Check-Out'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CheckOutDialog;
